import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import Pagination from './common/Pagination';
import usePagination from './common/usePagination';
import './Dashboard-enhanced.css';

// Get API URL from environment variable or use relative path for local development
const API_URL = process.env.REACT_APP_API_URL || '';
const AUTO_REFRESH_INTERVAL_MS = 60000;

const Dashboard = () => {
  const [, setTeams] = useState([]);
  const [overallMetrics, setOverallMetrics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [idleResources, setIdleResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const navigate = useNavigate();

  const teamDisplayNames = {
    'iCOP': 'iCOP',
    'IES': 'IES',
    'SMP': 'SMP',
    'IRMA': 'IRMA',
    'MyKONE': 'MyKONE Delivery Ops',
    'MyKONE-PSR': 'MyKONE PSR',
    'MyKONE-Platform': 'MyKONE Platform',
    'MyKONE-CIAM': 'MyKONE CIAM',
    'KONE-Support': 'KONE-Support'
  };

  // Cost Optimization Recommendations: sort the FULL list by team/env once, then
  // paginate it with the same DynamoDB-style numbered pagination used by the
  // Zafran JIRA tickets table (10 per page, jump-to-page, not "Load More").
  const sortedCostRecs = React.useMemo(() => {
    const list = recommendations?.recommendations;
    if (!Array.isArray(list)) return [];
    const teamOrder = ['iCOP', 'IES', 'SMP', 'IRMA', 'MyKONE', 'MyKONE-PSR', 'MyKONE-Platform', 'MyKONE-CIAM', 'KONE-Support'];
    const envOrder = { DEV: 1, QA: 2, ACC: 3, PROD: 4, SHARED: 5, SANDBOX: 6 };
    return [...list].sort((a, b) => {
      const ta = teamOrder.indexOf(a.team);
      const tb = teamOrder.indexOf(b.team);
      if (ta !== tb) return (ta === -1 ? 999 : ta) - (tb === -1 ? 999 : tb);
      return (envOrder[a.environment] || 999) - (envOrder[b.environment] || 999);
    });
  }, [recommendations]);

  const COST_RECS_PAGE_SIZE = 10;
  const {
    page: costRecsPage,
    setPage: setCostRecsPage,
    pageCount: costRecsPageCount,
    pageItems: costRecsPageItems,
    totalItems: costRecsTotal,
    pageSize: costRecsPageSize,
  } = usePagination(sortedCostRecs, COST_RECS_PAGE_SIZE, [sortedCostRecs]);

  // Team & Account Cost Analysis: free-text team search + 10-per-page pagination
  // (same DynamoDB-style numbered pagination used elsewhere). Searching matches
  // the team's display name, its raw key, account id or environment.
  const [accountSearch, setAccountSearch] = useState('');

  const filteredAccountDetails = React.useMemo(() => {
    const list = overallMetrics?.accountDetails;
    if (!Array.isArray(list)) return [];
    const q = accountSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => {
      const display = (teamDisplayNames[a.team] || a.team || '').toLowerCase();
      return (
        display.includes(q) ||
        (a.team || '').toLowerCase().includes(q) ||
        String(a.accountId || '').toLowerCase().includes(q) ||
        (a.environment || '').toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallMetrics, accountSearch]);

  const ACCOUNTS_PAGE_SIZE = 10;
  const {
    page: accountsPage,
    setPage: setAccountsPage,
    pageCount: accountsPageCount,
    pageItems: accountsPageItems,
    totalItems: accountsTotal,
    pageSize: accountsPageSize,
  } = usePagination(filteredAccountDetails, ACCOUNTS_PAGE_SIZE, [filteredAccountDetails]);

  // Custom label renderer for pie chart with text wrapping
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Shorten long service names
    const shortenName = (serviceName) => {
      const nameMap = {
        'Amazon Relational Database Service': 'RDS',
        'Amazon Elastic Compute Cloud': 'EC2',
        'Amazon Simple Storage Service': 'S3',
        'AWS Lambda': 'Lambda',
        'Amazon Virtual Private Cloud': 'VPC',
        'Amazon CloudWatch': 'CloudWatch',
        'Amazon DynamoDB': 'DynamoDB',
        'Amazon Elastic Load Balancing': 'ELB',
        'Amazon API Gateway': 'API Gateway',
        'Amazon CloudFront': 'CloudFront'
      };
      return nameMap[serviceName] || serviceName;
    };

    return (
      <text 
        x={x} 
        y={y} 
        fill="#333" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{ fontSize: '12px', fontWeight: '500' }}
      >
        {`${shortenName(name)}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  useEffect(() => {
    fetchData(true);

    const refreshTimer = setInterval(() => {
      fetchData(false);
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(refreshTimer);
    };
  }, []);

  // Add/remove body class when loading
  useEffect(() => {
    if (loading) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }
    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading]);

  // Fetch all data in parallel (all from S3 cache - should be fast)
  const fetchData = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }
    setError(null);

    console.log('=== Dashboard fetchData called ===');
    console.log('API_URL:', API_URL);
    
    try {
      // Fetch all data in parallel for faster loading
      const [teamsRes, metricsRes, recommendationsRes, idleResourcesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/teams`),
        axios.get(`${API_URL}/api/metrics/overall`),
        axios.get(`${API_URL}/api/recommendations`),
        axios.get(`${API_URL}/api/idle-resources`)
      ]);
      
      // Process teams response
      if (teamsRes.status === 'fulfilled') {
        console.log('Teams loaded:', teamsRes.value.data);
        setTeams(teamsRes.value.data);
        setWarning(null);
      } else {
        console.error('Error fetching teams:', teamsRes.reason);
        const statusCode = teamsRes.reason?.response?.status;
        const backendMessage = teamsRes.reason?.response?.data?.error;
        if (statusCode === 401 || statusCode === 403) {
          setWarning(backendMessage || 'SSO authorization failed. Auto-refresh will retry after re-authentication.');
        } else {
          setWarning('Teams data is temporarily unavailable. Auto-refresh will retry.');
        }
      }
      
      // Process metrics response
      if (metricsRes.status === 'fulfilled') {
        console.log('Metrics loaded:', metricsRes.value.data);
        setOverallMetrics(metricsRes.value.data);
      } else {
        console.error('Error fetching metrics:', metricsRes.reason);
      }
      
      // Process recommendations response
      if (recommendationsRes.status === 'fulfilled') {
        console.log('Recommendations loaded:', recommendationsRes.value.data);
        setRecommendations(recommendationsRes.value.data);
      } else {
        console.error('Error fetching recommendations:', recommendationsRes.reason);
      }

      // Process idle-resources roll-up (account-level counts across all teams/envs)
      if (idleResourcesRes.status === 'fulfilled') {
        console.log('Idle resources loaded:', idleResourcesRes.value.data);
        setIdleResources(idleResourcesRes.value.data);
      } else {
        console.error('Error fetching idle resources:', idleResourcesRes.reason);
      }

      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      console.log('=== Dashboard fetchData completed ===');
      setLastRefreshedAt(new Date());
      setLoading(false);
    }
  };

  const handleAccountClick = (teamName, environment) => {
    navigate(`/team/${teamName}/${environment}`);
  };

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-message" style={{padding: '20px', color: '#E53935', backgroundColor: '#FFEBEE', borderRadius: '8px', margin: '20px'}}>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <p>Check browser console (F12) for details.</p>
          <button className="btn-primary" onClick={() => fetchData(true)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const COLORS = ['#0051A3', '#00A3E0', '#FF9800', '#E53935', '#00A651', '#9C27B0', '#FF5722', '#607D8B', '#795548', '#009688'];
  
  // Special color for "Others" category (grey to indicate aggregated services)
  const OTHERS_COLOR = '#9E9E9E';

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading dashboard...</div>
          <div className="loading-subtext">Fetching data from cache</div>
        </div>
      )}
      <div className="dashboard">
      {warning && (
        <div className="error-message" style={{padding: '12px 16px', color: '#92400E', backgroundColor: '#FEF3C7', borderRadius: '8px', margin: '0 0 16px 0'}}>
          {warning}
        </div>
      )}
      {lastRefreshedAt && (
        <div style={{fontSize: '12px', color: '#6B7280', marginBottom: '12px'}}>
          Last refreshed: {lastRefreshedAt.toLocaleTimeString()} (auto-refresh every 60s)
        </div>
      )}
      {/* Overall Monitoring Section */}
      <section className="overall-metrics">
        <h2>Overall Monitoring</h2>
        {overallMetrics ? (
          <>
            {/* Cost Analysis Charts - SHOWN FIRST */}
            {(overallMetrics.sixMonthTrend?.length > 0 || overallMetrics.costDistribution?.length > 0) && (
              <div className="cost-analysis-section">
                <h3 className="section-subtitle">
                  📈 Cost Trends & Distribution
                  <span className="info-icon-wrapper">
                    <span 
                      className="info-icon"
                      title="Cost data is cached and updated periodically based on environment"
                    >
                      ℹ️
                    </span>
                    <span className="info-tooltip info-tooltip-blue">
                      <strong>About Cost Data:</strong><br/>
                      {overallMetrics.dataFreshness.environment === 'dev' ? (
                        <>
                          Cost information is cached and updated <strong>monthly on the 1st at 1 AM UTC</strong> in the Development environment.
                        </>
                      ) : (
                        <>
                          Cost information is cached and updated <strong>every 3 days at 1 AM UTC</strong> in the Production environment.
                        </>
                      )}
                      <br/><br/>
                      <em>Note: Infrastructure metrics are fetched in real-time. Cost recommendations are collected daily.</em>
                    </span>
                  </span>
                </h3>
                <div className="charts-grid-main">
                  {/* 6-Month Service Cost Distribution - Main Chart */}
                  {overallMetrics.sixMonthServiceCosts && overallMetrics.sixMonthServiceCosts.length > 0 && (() => {
                    // Prepare chart data with totals
                    const chartData = overallMetrics.sixMonthServiceCosts.map(monthData => {
                      const dataPoint = { month: monthData.month };
                      let total = 0;
                      monthData.services.forEach(service => {
                        dataPoint[service.name] = service.value;
                        total += service.value;
                      });
                      dataPoint._total = total;
                      return dataPoint;
                    });
                    
                    // Get all unique services
                    const allServices = new Set();
                    overallMetrics.sixMonthServiceCosts.forEach(monthData => {
                      monthData.services.forEach(service => {
                        allServices.add(service.name);
                      });
                    });
                    const servicesArray = Array.from(allServices);
                    
                    const serviceNameMap = {
                      'Amazon Relational Database Service': 'RDS',
                      'Amazon Elastic Compute Cloud': 'EC2',
                      'Amazon Simple Storage Service': 'S3',
                      'AWS Lambda': 'Lambda',
                      'Amazon Virtual Private Cloud': 'VPC',
                      'Amazon CloudWatch': 'CloudWatch',
                      'Amazon DynamoDB': 'DynamoDB',
                      'Amazon Elastic Load Balancing': 'ELB',
                      'Amazon API Gateway': 'API Gateway',
                      'Amazon CloudFront': 'CloudFront'
                    };
                    
                    // Custom label for showing total on the topmost bar segment
                    const renderTotalLabel = (props) => {
                      const { x, y, width, index } = props;
                      const total = chartData[index]._total;
                      const formattedTotal = total >= 1000 ? `$${(total / 1000).toFixed(1)}k` : `$${Math.round(total)}`;
                      return (
                        <text 
                          x={x + width / 2} 
                          y={y - 8} 
                          fill="#1976d2" 
                          textAnchor="middle" 
                          fontSize="11" 
                          fontWeight="700"
                        >
                          {formattedTotal}
                        </text>
                      );
                    };
                    
                    return (
                      <div className="chart-card" style={{gridColumn: 'span 2'}}>
                        <h4>
                          Service Cost Distribution (Last 6 Months)
                          {overallMetrics.costDistributionContext?.type === 'single_team' 
                            ? ` - ${teamDisplayNames[overallMetrics.costDistributionContext.team] || overallMetrics.costDistributionContext.team}`
                            : ''}
                        </h4>
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart 
                            data={chartData}
                            margin={{ top: 25, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="month" stroke="#666" style={{fontSize: '12px'}} />
                            <YAxis stroke="#666" style={{fontSize: '12px'}} tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                            <Tooltip 
                              contentStyle={{backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px'}}
                              formatter={(value, name) => name === '_total' ? null : `$${value.toLocaleString()}`}
                            />
                            <Legend wrapperStyle={{paddingTop: '10px'}} />
                            {servicesArray.map((service, index) => {
                              const isLastService = index === servicesArray.length - 1;
                              return (
                                <Bar 
                                  key={service} 
                                  dataKey={service} 
                                  stackId="a" 
                                  fill={service === 'Others' ? OTHERS_COLOR : COLORS[index % COLORS.length]}
                                  name={serviceNameMap[service] || service}
                                >
                                  {isLastService && <LabelList dataKey="_total" content={renderTotalLabel} />}
                                </Bar>
                              );
                            })}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })()}

                  {/* Fallback: Cost by Service Pie Chart (if no 6-month data) */}
                  {(!overallMetrics.sixMonthServiceCosts || overallMetrics.sixMonthServiceCosts.length === 0) && 
                   overallMetrics.costDistribution && overallMetrics.costDistribution.length > 0 && (
                    <div className="chart-card" style={{gridColumn: 'span 2'}}>
                      <h4>
                        Cost by Service
                        {overallMetrics.costDistributionContext?.type === 'single_team' 
                          ? ` - ${teamDisplayNames[overallMetrics.costDistributionContext.team] || overallMetrics.costDistributionContext.team}`
                          : ''}
                        {overallMetrics.isEarlyMonth ? ' (Last Month)' : ' (Current Month)'}
                      </h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={overallMetrics.costDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={renderCustomLabel}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {overallMetrics.costDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Account-wise Cost Table - SHOWN SECOND */}
            {overallMetrics.accountDetails && overallMetrics.accountDetails.length > 0 && (
              <div className="cost-analysis-section">
                <h3 className="section-subtitle">
                  💰 {overallMetrics.costDistributionContext?.type === 'single_team' 
                    ? `${teamDisplayNames[overallMetrics.costDistributionContext.team] || overallMetrics.costDistributionContext.team} Account Cost Analysis`
                    : 'Team & Account Cost Analysis'}
                  <span className="info-icon-wrapper">
                    <span 
                      className="info-icon"
                      title="Cost data is cached and updated periodically based on environment"
                    >
                      ℹ️
                    </span>
                    <span className="info-tooltip info-tooltip-blue">
                      <strong>About Cost Data:</strong><br/>
                      {overallMetrics.dataFreshness?.environment === 'dev' ? (
                        <>
                          Cost information is cached and updated <strong>monthly on the 1st at 1 AM UTC</strong> in the Development environment.
                        </>
                      ) : (
                        <>
                          Cost information is cached and updated <strong>every 3 days at 1 AM UTC</strong> in the Production environment.
                        </>
                      )}
                      <br/><br/>
                      <em>Note: Infrastructure metrics are fetched in real-time. Cost recommendations are collected daily.</em>
                    </span>
                  </span>
                </h3>

                <div className="cost-table-search">
                  <input
                    type="text"
                    className="cost-table-search-input"
                    placeholder="Search team, account ID or environment…"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    aria-label="Search accounts"
                  />
                  {accountSearch && (
                    <button
                      type="button"
                      className="cost-table-search-clear"
                      onClick={() => setAccountSearch('')}
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="cost-table-container">
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>Team</th>
                        <th>Environment</th>
                        <th>Account ID</th>
                        <th>Current Month</th>
                        <th>Last Month</th>
                        <th>
                          Forecast
                          <span className="info-icon-wrapper">
                            <span 
                              className="info-icon"
                              title="End-of-month cost forecast based on current spending trends and AWS Cost Explorer predictions"
                            >
                              ℹ️
                            </span>
                            <span className="info-tooltip">
                              <strong>Forecast Explanation:</strong><br/>
                              This is the projected total cost for the end of the current month, calculated by AWS Cost Explorer based on:
                              <ul>
                                <li>Current month-to-date spending patterns</li>
                                <li>Historical usage trends</li>
                                <li>Seasonal variations</li>
                                <li>AWS machine learning predictions</li>
                              </ul>
                              The forecast helps you anticipate your final monthly bill and identify potential cost overruns early.
                            </span>
                          </span>
                        </th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountsPageItems.map((account, index) => {
                        // First row of a new team within the current page (used
                        // only for the subtle team divider styling).
                        const isFirstInTeam = index === 0 ||
                          accountsPageItems[index - 1].team !== account.team;
                        
                        return (
                          <tr 
                            key={index}
                            className={`clickable-row ${isFirstInTeam ? 'team-first-row' : ''}`}
                            onClick={() => handleAccountClick(account.team, account.environment.toLowerCase())}
                          >
                            <td className="team-name">{teamDisplayNames[account.team] || account.team}</td>
                            <td>
                              <span className={`env-badge-small env-${account.environment.toLowerCase()}`}>
                                {account.environment}
                              </span>
                            </td>
                            <td className="account-id">{account.accountId}</td>
                            <td className="cost-value">${account.currentMonth.toLocaleString()}</td>
                            <td className="cost-value">${account.lastMonth.toLocaleString()}</td>
                            <td className="forecast-value">${account.forecast.toLocaleString()}</td>
                            <td className={`change-value ${account.changePercent >= 0 ? 'positive' : 'negative'}`}>
                              {account.changePercent >= 0 ? '+' : ''}{account.changePercent}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {accountsTotal === 0 && (
                    <div className="cost-table-empty">
                      No accounts match "{accountSearch}".
                    </div>
                  )}
                </div>

                <Pagination
                  page={accountsPage}
                  pageCount={accountsPageCount}
                  totalItems={accountsTotal}
                  pageSize={accountsPageSize}
                  onPageChange={setAccountsPage}
                />
              </div>
            )}
          </>
        ) : (
          <div className="metrics-loading">
            <p>Cost and metrics data unavailable</p>
            <button onClick={() => fetchData(true)} className="retry-button">Retry</button>
          </div>
        )}
      </section>


      {/* Cost Optimization Recommendations Section */}
      {recommendations && recommendations.recommendations && recommendations.recommendations.length > 0 && (
        <section className="recommendations-section cost-section">
          <h2>💡 Cost Optimization Recommendations</h2>
          <div className="recommendations-summary">
            <div className="summary-card">
              <div className="summary-value">
                {recommendations.recommendations.length}
              </div>
              <div className="summary-label">Total Recommendations</div>
            </div>
            <div className="summary-card highlight">
              <div className="summary-value">${recommendations.totalPotentialSavings?.toLocaleString() || 0}/mo</div>
              <div className="summary-label">Potential Savings</div>
            </div>
          </div>

          <div className="recommendations-table-container">
            <table className="recommendations-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Team / Environment</th>
                  <th>Account ID</th>
                  <th>Region</th>
                  <th>Service</th>
                  <th>Category</th>
                  <th>Recommendation</th>
                  <th>Potential Savings</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Rows for the CURRENT page only (already sorted by team/env).
                  const pageRecommendations = costRecsPageItems;

                  // Render every row with its OWN Team/Environment and Account ID
                  // (no rowSpan cell-merging), so each recommendation is fully
                  // self-describing even when one account has several rows. A thin
                  // top border marks where a new team/env group starts.
                  let prevGroupKey = null;
                  return pageRecommendations.map((rec, index) => {
                    const groupKey = `${rec.team}-${rec.environment}`;
                    const isGroupFirst = groupKey !== prevGroupKey;
                    prevGroupKey = groupKey;
                    return (
                      <tr key={`${groupKey}-${index}`} className={`priority-${rec.priority} ${isGroupFirst ? 'group-first' : ''}`}>
                        <td>
                          <span className={`priority-badge priority-${rec.priority}`}>
                            {rec.priority.toUpperCase()}
                          </span>
                        </td>
                        <td className="team-cell">
                          <div className="team-name">{teamDisplayNames[rec.team] || rec.team}</div>
                          <span className={`env-badge-small env-${(rec.environment || '').toLowerCase()}`}>
                            {rec.environment}
                          </span>
                        </td>
                        <td className="account-id">{rec.accountId}</td>
                        <td>{rec.region}</td>
                        <td className="service-name">{rec.service}</td>
                        <td className="category-name">{rec.category}</td>
                        <td className="recommendation-details">
                          <div className="recommendation-title">{rec.title}</div>
                          <div className="recommendation-description">{rec.description}</div>
                        </td>
                        <td className="savings-value">
                          {rec.savings > 0 ? `$${rec.savings.toLocaleString()}/mo` : '-'}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          <Pagination
            page={costRecsPage}
            pageCount={costRecsPageCount}
            totalItems={costRecsTotal}
            pageSize={costRecsPageSize}
            onPageChange={setCostRecsPage}
          />
          <div className="recommendations-footer">
            Last updated: {new Date(recommendations.lastUpdated).toLocaleString()}
          </div>
        </section>
      )}

      {/* Idle Resources (account-level roll-up) - mirrors the Cost Optimization
          Recommendations layout. One row per account with its idle-resource
          count and age-bucket breakdown. Fed by GET /api/idle-resources. */}
      {idleResources && idleResources.accounts && idleResources.accounts.length > 0 && (
        <section className="recommendations-section cost-section idle-section">
          <h2>🧹 Idle Resources</h2>
          <div className="recommendations-summary">
            <div className="summary-card">
              <div className="summary-value">{idleResources.totalIdleResources || 0}</div>
              <div className="summary-label">Total Idle Resources</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">{idleResources.accountCount || idleResources.accounts.length}</div>
              <div className="summary-label">Accounts Scanned</div>
            </div>
            <div className="summary-card highlight">
              <div className="summary-value">{idleResources.totalIdleByBucket?.['1y'] || 0}</div>
              <div className="summary-label">Idle ≥ 1 year</div>
            </div>
          </div>

          <div className="recommendations-table-container">
            <table className="recommendations-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Accounts</th>
                  <th>Idle Resources</th>
                  <th>Idle ≥ 3 months</th>
                  <th>Idle ≥ 6 months</th>
                  <th>Idle ≥ 1 year</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const teamOrder = ['iCOP', 'IES', 'SMP', 'MyKONE', 'MyKONE-PSR', 'MyKONE-Platform', 'MyKONE-CIAM', 'KONE-Support'];

                  // Aggregate the per-account rows into ONE row per team, summing
                  // the idle counts and age buckets across all of the team's
                  // environments/accounts.
                  const byTeam = {};
                  (idleResources.accounts || []).forEach((acct) => {
                    const team = acct.team || 'Unknown';
                    if (!byTeam[team]) {
                      byTeam[team] = {
                        team,
                        accountCount: 0,
                        idleResourceCount: 0,
                        buckets: { '3m': 0, '6m': 0, '1y': 0 },
                      };
                    }
                    const t = byTeam[team];
                    t.accountCount += 1;
                    t.idleResourceCount += acct.idleResourceCount || 0;
                    const b = acct.idleCountByBucket || {};
                    t.buckets['3m'] += b['3m'] || 0;
                    t.buckets['6m'] += b['6m'] || 0;
                    t.buckets['1y'] += b['1y'] || 0;
                  });

                  const teamRows = Object.values(byTeam).sort((a, b) => {
                    const ia = teamOrder.indexOf(a.team);
                    const ib = teamOrder.indexOf(b.team);
                    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                  });

                  return teamRows.map((row, index) => (
                    <tr key={`${row.team}-${index}`}>
                      <td className="team-cell">
                        <div className="team-name">{teamDisplayNames[row.team] || row.team}</div>
                      </td>
                      <td className="account-id">{row.accountCount}</td>
                      <td className="savings-value">{row.idleResourceCount || 0}</td>
                      <td>{row.buckets['3m'] || 0}</td>
                      <td>{row.buckets['6m'] || 0}</td>
                      <td>{row.buckets['1y'] || 0}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          {idleResources.lastUpdated && (
            <div className="recommendations-footer">
              Last updated: {new Date(idleResources.lastUpdated).toLocaleString()}
            </div>
          )}
        </section>
      )}
      </div>
    </>
  );
};

export default Dashboard;

